import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Spin, Button } from '@bedrock/components';
import { ChevronThinLeft } from '@bedrock/icons-react';
import { useRequest } from 'ahooks';
import useLayoutContentWidth from '@mpc/common/src/hooks/useLayoutContentWidth';
import { fetchAccidentDetail } from '../services/index';
import '@purchase/supplier/dist/index.css';
import type { AccidentDetailData } from './constants';
import ContentBox from './ContentBox';
import './index.less';

/**
 * 协同端事故详情页。
 *
 * UI 对齐内包（ContentBox + FormCard/FormDescriptions 体系，纵向堆叠无 Tab）；
 * 外层容器对齐协同端 SupplierDetail：Spin.Global + useLayoutContentWidth（1288 自适应）。
 * 宽/左随视口动态放 inline style；top/height/灰底/内部滚动放 index.less 的 .accident-detail-view
 * （px 计算，不用 rem——calc 混 rem 在根字号非 1px 时高度失真，且内容会溢出灰盒盖白）。
 * 数据源 fetchAccidentDetail（走 invoker cooperation.accident.detail）。
 */
const AccidentDetail: React.FC = () => {
  const { accidentId } = useParams<{ accidentId: string }>();
  const history = useHistory();
  const layoutContentWidth = useLayoutContentWidth();

  const { data: detailData, loading } = useRequest(async () => {
    const res = await fetchAccidentDetail({ accidentId: Number(accidentId) });
    return res?.data;
  });

  const detail = detailData as AccidentDetailData | undefined;

  const handleGoBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.replace('/outCompanyManagement/outCompanyInfo');
    }
  }, [history]);

  const styles: React.CSSProperties = {
    // 级联兜底：Spin.Global 会带上 .rock-spin-global-absolute（position:absolute），
    // 依赖 less 覆盖有顺序风险，此处内联 fixed + 显式 top:0 保证左/上/右贴边。
    // bottom:'auto' 解除 .rock-spin-global 自带 bottom:0 的拉伸——否则 fixed 容器
    // 被拉满视口高，内容不足一屏时底部留出大块灰底
    position: 'fixed',
    top: 0,
    bottom: 'auto',
    width: layoutContentWidth + 'px',
    left: window.innerWidth - layoutContentWidth + 'px',
  };

  const headerTitle = `供应商执行事故处理评估-${detail?.vendorName || '--'}-${detail?.accidentNo}`;

  return (
    <Spin.Global spinning={loading} className="accident-detail-view" style={styles}>
      {/* 头部：跳出滚动容器（.accident-detail-view-body）单独存在，钉在灰底容器顶部，
          左/上/右填满容器；列表滚动时始终可见（shrink-0 防被压缩） */}
      {detail && (
        <div className="bg-white px-4 pt-4 pb-2 box-border shrink-0">
          <div className="flex items-center">
            <Button
              type="checked-neutral"
              size="small"
              aria-label="返回"
              className="mr-3 shrink-0"
              onClick={handleGoBack}
              icon={<ChevronThinLeft />}
            />
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold text-color-1 m-0 truncate">{headerTitle}</h1>
            </div>
          </div>
        </div>
      )}
      <div className="accident-detail-view-body">
        <ContentBox data={detail as AccidentDetailData} />
      </div>
    </Spin.Global>
  );
};

export default AccidentDetail;
